import { svaraApi } from '../utils/axiosInstance';

/**
 * Private chat: the 1:1 text chat a donation unlocks between two players. Mirrors the
 * `/api/chat` endpoints of gateway-game (DONATIONS-CONTRACT.md). Every id is a string and every
 * time an ISO string.
 *
 * Errors come back as plain axios errors: 404 when the caller is not in the conversation, 403
 * when either player blocked the other (or the sender is banned), 400 for empty or too-long text
 * and 429 when the sender went over 10 messages in 10 seconds.
 */
export type ChatPlayer = {
	id: string;
	username: string;
	avatarFileId: string | null;
	isVip: boolean;
};

export type ChatMessage = {
	id: string;
	conversationId: string;
	senderId: string;
	text: string;
	createdAt: string;
};

export type ConversationSummary = {
	id: string;
	otherPlayer: ChatPlayer;
	lastMessage: ChatMessage | null;
	unreadCount: number;
	unlockedAt: string;
};

/** The profile's view of a pair: whether a donation has unlocked their chat yet. */
export type ConversationWithPlayer = {
	conversationId: string | null;
	unlocked: boolean;
};

export type ChatMessagesPage = {
	/** Oldest first within the page. */
	items: ChatMessage[];
	hasMore: boolean;
};

/** The server trims and then accepts 1..500 characters. */
export const CHAT_MESSAGE_MAX_LENGTH = 500;
/** The server's default page; it accepts 1..50. */
export const CHAT_PAGE_SIZE = 30;

/** Socket events pushed on the `/game` socket through `gateway.notify.player`. */
export const PRIVATE_MESSAGE_EVENT = 'privateMessage';
export const PRIVATE_CHAT_UNLOCKED_EVENT = 'privateChatUnlocked';

export type PrivateChatUnlockedPacket = {
	conversationId: string;
	otherPlayer: ChatPlayer;
};

/** Newest activity first. */
export async function getConversations(): Promise<{ items: ConversationSummary[] }> {
	const response = await svaraApi.get('/api/chat/conversations');
	return response.data;
}

export async function getConversationWithPlayer(playerId: string): Promise<ConversationWithPlayer> {
	const response = await svaraApi.get(
		`/api/chat/conversations/with/${encodeURIComponent(playerId)}`,
	);
	return response.data;
}

/** `before` pages backwards from that message time; left out, the newest page. */
export async function getConversationMessages(
	conversationId: string,
	params: { before?: string; limit?: number } = {},
): Promise<ChatMessagesPage> {
	const response = await svaraApi.get(
		`/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
		{
			params: {
				limit: params.limit ?? CHAT_PAGE_SIZE,
				...(params.before ? { before: params.before } : {}),
			},
		},
	);
	return response.data;
}

export async function sendConversationMessage(
	conversationId: string,
	text: string,
): Promise<ChatMessage> {
	const response = await svaraApi.post(
		`/api/chat/conversations/${encodeURIComponent(conversationId)}/messages`,
		{ text },
	);
	return response.data;
}

export async function markConversationRead(conversationId: string): Promise<{ unreadCount: 0 }> {
	const response = await svaraApi.post(
		`/api/chat/conversations/${encodeURIComponent(conversationId)}/read`,
	);
	return response.data;
}

/** The total across every conversation. */
export async function getChatUnreadCount(): Promise<{ count: number }> {
	const response = await svaraApi.get('/api/chat/unread-count');
	return response.data;
}
